import { formatDate } from 'helpers/dates-formatter';

import { ROLES } from 'constants/roles';
import { STATES } from 'constants/states';

export interface SelectOption<TValue = string | number> {
  label: string;
  value: TValue;
  consultantId?: string | number;
}

interface UserLike {
  id: string | number;
  name: string;
  [key: string]: unknown;
}

interface ClientLike {
  id: string | number;
  name: string;
  externalId?: string | number;
  state?: string;
  authorizedDomains?: string;
  expireDate?: string;
  consultants: ConsultantLike[];
  [key: string]: unknown;
}

interface ConsultantLike {
  id: string | number;
  user?: UserLike;
  consultantType: string;
}

interface RoleOption {
  label: string;
  radioValue: string | number;
}

export const mapUserToSelectOption = (
  user: UserLike | null | undefined
): SelectOption | undefined =>
  user
    ? {
        label: user.name,
        value: user.id,
      }
    : undefined;

export const mapUsersToSelectOptions = (
  users: UserLike[] | null | undefined
): (SelectOption | undefined)[] | undefined =>
  users ? users.map((user) => mapUserToSelectOption(user)) : undefined;

export const mapClientToSelectOption = (
  client: ClientLike | null | undefined
): SelectOption | undefined =>
  client
    ? {
        label: client.name,
        value: client.id,
      }
    : undefined;

export const mapClientsToSelectOptions = (
  clients: ClientLike[] | null | undefined
): (SelectOption | undefined)[] | undefined =>
  clients
    ? clients.map((client) => mapClientToSelectOption(client))
    : undefined;

export const findUser = (
  userId: string | number,
  users: UserLike[]
): UserLike | undefined => users.find((u) => u.id === userId);

export const isPrimitive = (value: unknown): boolean =>
  typeof value !== 'object';

export const mapRoleToSelectOptions = (
  role: RoleOption | null | undefined
): SelectOption | undefined =>
  role
    ? {
        label: role.label,
        value: role.radioValue,
      }
    : undefined;

export const mapRolesToSelectOptions = (
  roles: RoleOption[] | null | undefined
): (SelectOption | undefined)[] | undefined =>
  roles ? roles.map((role) => mapRoleToSelectOptions(role)) : undefined;

export const mapDomainsToSelectOptions = (
  domainsString: string | null | undefined
): SelectOption[] | undefined =>
  domainsString
    ? domainsString
        .split(',')
        .map((domain) => ({ label: domain, value: domain }))
    : undefined;

export const withoutNulls = <T extends Record<string, unknown>>(
  obj: T
): Partial<T> => {
  const objWithoutNulls: Partial<T> = { ...obj };

  (Object.keys(objWithoutNulls) as Array<keyof T>).forEach((key) => {
    if (objWithoutNulls[key] == null) delete objWithoutNulls[key];
  });

  return objWithoutNulls;
};

export const toNumber = (value: string | number): string =>
  value.toString().replace(/\D/g, '');

export const flattenObject = (
  obj: Record<string, unknown>,
  prefix = ''
): Record<string, unknown> =>
  Object.keys(obj).reduce<Record<string, unknown>>((acc, k) => {
    const pre = prefix.length ? prefix + '.' : '';
    const value = obj[k];

    if (typeof value === 'object' && value !== null)
      Object.assign(
        acc,
        flattenObject(value as Record<string, unknown>, pre + k)
      );
    else acc[pre + k] = value;

    return acc;
  }, {});

export const consultantNameByRole = (
  client: ClientLike,
  consultantRole: string
): string => {
  const consultant = client.consultants.find(
    (c) => c.consultantType === consultantRole
  );

  if (!consultant) return '';

  return consultant?.user?.name || '';
};

interface ConsultantData {
  user: { id: string | number };
  clientId: string | number;
  consultantType: string;
}

const createConsultantData = (
  type: string,
  formConsultant: string | number | undefined,
  clientId: string | number = ''
): ConsultantData | undefined => {
  if (!formConsultant) return undefined;

  const consultantData: ConsultantData = {
    user: { id: formConsultant },
    clientId: clientId,
    consultantType: type.toUpperCase(),
  };

  if (clientId) consultantData.clientId = clientId;

  return consultantData;
};

export interface ProfileFormValues {
  id?: string | number;
  authorizedDomains: SelectOption[];
  principalConsultant?: SelectOption;
  contactConsultant?: SelectOption;
  analystConsultant?: SelectOption;
  expireDate?: string;
  status?: string;
  externalId: string | number;
  name: string;
  state: SelectOption;
}

export const transformProfileFormData = (
  formValues: ProfileFormValues
): Record<string, unknown> => {
  const values: Record<string, unknown> = {};

  if (formValues.id) values.id = formValues.id;

  values.authorizedDomains = formValues.authorizedDomains
    .map((domain) => domain.value)
    .join(',');

  values.consultants = [
    createConsultantData(
      'principal',
      formValues.principalConsultant?.value as string | number | undefined,
      formValues?.id
    ),
    createConsultantData(
      'contact',
      formValues.contactConsultant?.value as string | number | undefined,
      formValues?.id
    ),
    createConsultantData(
      'analyst',
      formValues.analystConsultant?.value as string | number | undefined,
      formValues?.id
    ),
  ].filter((c) => c);

  const expireDate =
    formValues.expireDate && formValues.expireDate !== 'expireDate'
      ? formValues.expireDate
      : '01/01/2100';

  values.expireDate = formatDate(expireDate, 'YYYY-MM-DD');

  if (formValues.status) values.status = formValues.status;

  values.externalId = Number(formValues.externalId);
  values.name = formValues.name;
  values.state = formValues.state.value;

  return values;
};

export const transformClientToFormObject = (
  client: ClientLike
): Record<string, unknown> => {
  /**
  We get consultants like: [{id: 2, user: {id: 1}, clientId: 54, consultantType: "PRINCIPAL"}]
  and we want to transform them into objects that match our form:
    {
      principal: {label: 'first last', value: userId}
    }
  */
  const transformedConsultants = (client.consultants || []).reduce<
    Record<string, SelectOption>
  >((formObj, consultant) => {
    const consultantUser = consultant.user;
    const asSelectOptions = mapUserToSelectOption(consultantUser);

    if (asSelectOptions) {
      asSelectOptions.consultantId = consultant.id;

      formObj[`${consultant.consultantType.toLowerCase()}Consultant`] =
        asSelectOptions;
    }

    return formObj;
  }, {});

  const transformed = {
    id: client.id,
    externalId: Number(client.externalId),
    name: client.name,
    state: STATES.find(
      (state: { value: string }) => state.value === client.state
    ),
    authorizedDomains: mapDomainsToSelectOptions(client?.authorizedDomains),
    expireDate: formatDate(client.expireDate, 'MM/DD/YYYY'),
    ...transformedConsultants,
  };

  return transformed;
};

export interface UserFormValues {
  id?: string | number;
  firstName: string;
  lastName: string;
  jobTitle: string;
  email: string;
  role: string;
  client?: SelectOption;
}

export const transformUserFormData = (
  formValues: UserFormValues,
  currentUser?: { id?: string | number } | null
): Record<string, unknown> => {
  const { firstName, lastName, jobTitle, email, role } = formValues;

  const userObject: Record<string, unknown> = {
    firstName,
    lastName,
    jobTitle,
    email,
    client: null,
  };

  if (formValues?.id) userObject.id = formValues.id;

  if (formValues?.client?.value) {
    userObject.client = { id: formValues.client.value };
  }

  if (currentUser?.id !== formValues.id) {
    switch (role) {
      case 'superAdmin':
      case 'Super Admin':
        userObject.authority = ROLES.SUPER_ADMIN;
        break;
      case 'fdPrincipal':
      case 'MV Principal':
        userObject.authority = ROLES.FD_PRINCIPAL;
        break;
      case 'fdClientService':
      case 'MV Client Service':
        userObject.authority = ROLES.FD_CLIENT_SERVICE;
        break;
      case 'fdAnalyst':
      case 'MV Analyst':
        userObject.authority = ROLES.FD_ANALYST;
        break;
      case 'admin':
        userObject.authority = (ROLES as Record<string, string>).CLIENT_ADMIN;
        break;
      case 'internalUser':
      case 'Internal User':
        userObject.authority = ROLES.INTERNAL_USER;
        break;
      case 'reportAdmin':
      case 'Report Admin':
        userObject.authority = ROLES.REPORT_ADMIN;
        break;
      default:
        userObject.authority = ROLES.USER;
        break;
    }
  }

  return userObject;
};

export const getRoleNameFromAuthorities = (
  authority: string | undefined
): string => {
  if (authority === ROLES.SUPER_ADMIN) {
    return 'Super Admin';
  }
  if (authority === ROLES.FD_PRINCIPAL) {
    return 'MV Principal';
  }
  if (authority === ROLES.FD_CLIENT_SERVICE) {
    return 'MV Client Service';
  }
  if (authority === ROLES.FD_ANALYST) {
    return 'MV Analyst';
  }
  if (authority === ROLES.INTERNAL_USER) {
    return 'internalUser';
  }
  if (authority === ROLES.REPORT_ADMIN) {
    return 'reportAdmin';
  }

  return 'user';
};

interface UserWithAuthority {
  id?: string | number;
  firstName?: string;
  lastName?: string;
  jobTitle?: string;
  email?: string;
  authority?: string;
  client?: ClientLike;
}

export const transformUserToFormObject = (
  user: UserWithAuthority | null | undefined,
  client: ClientLike | null = null
): Record<string, unknown> => {
  let defaultUserData: Record<string, unknown> = {
    firstName: '',
    lastName: '',
    jobTitle: '',
    email: '',
    confirmEmail: '',
    role: getRoleNameFromAuthorities(user?.authority),
    client: client && mapClientToSelectOption(client),
  };

  if (!!user)
    defaultUserData = {
      id: user?.id,
      firstName: user?.firstName,
      lastName: user?.lastName,
      jobTitle: user?.jobTitle,
      email: user?.email,
      role: getRoleNameFromAuthorities(user?.authority),
      client: mapClientToSelectOption(user.client),
    };

  return defaultUserData;
};

export interface ServerFile {
  name: string;
  fileName: string;
  fileSize: number;
  fileExt: string;
  uploadDate: string;
  description: string;
}

export const transformFileToFormObject = ({
  name,
  fileName,
  fileSize,
  fileExt,
  uploadDate,
  description,
}: ServerFile): Record<string, unknown> => ({
  fileName: { value: name, label: name },
  description,
  file: {
    name: `${fileName}.${fileExt}`,
    size: fileSize,
    uploadDate,
    isFakeFile: true,
  },
});
